import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  collection,
  addDoc
} from "firebase/firestore";
import { db } from "../firebase";

/* =========================================
   CONSTANTS
========================================= */

const DAILY_POINT_CAP = 100;
const NEW_ACCOUNT_DAILY_CAP = 40;
const COMMENT_COOLDOWN_MS = 2 * 60 * 1000;
const LIKE_SAVE_COOLDOWN_MS = 20 * 1000;
const NEW_ACCOUNT_DAYS = 7;

/* =========================================
   THEME DEFINITIONS
   Each theme has: id, name, description,
   tier, unlockRequirement, variables (CSS)
========================================= */

/** All CSS custom properties themes may set on :root */
export const THEME_CSS_VAR_KEYS = [
  "--bg",
  "--surface",
  "--surface-2",
  "--border",
  "--text",
  "--text-muted",
  "--muted",
  "--light-muted",
  "--sidebar-bg",
  "--card-bg",
  "--nav-active-bg",
  "--nav-active-color",
  "--purple-soft",
];

export const THEMES = {
  warmCream: {
    id: "warmCream",
    name: "Warm Cream",
    description: "Cozy and nostalgic — the default",
    tier: 0,
    unlockRequirement: null,
    free: true,
    variables: {
      "--bg": "#F2EFE9",
      "--surface": "#FFFFFF",
      "--surface-2": "#EDE9E2",
      "--border": "#DDD8CE",
      "--text": "#0E0C0A",
      "--text-muted": "#9A9088",
      "--muted": "#9A9088",
      "--light-muted": "#9A9088",
      "--sidebar-bg": "#EDE9E2",
      "--card-bg": "#FFFFFF",
      "--nav-active-bg": "#FDF3E0",
      "--nav-active-color": "#C4790A",
      "--purple-soft": "#FDF3E0",
    }
  },
  cleanWhite: {
    id: "cleanWhite",
    name: "Clean White",
    description: "Minimal and modern",
    tier: 0,
    unlockRequirement: null,
    free: true,
    variables: {
      "--bg": "#FAFAFA",
      "--surface": "#FFFFFF",
      "--surface-2": "#F0F0F0",
      "--border": "#E8E8E8",
      "--text": "#0E0C0A",
      "--text-muted": "#999999",
      "--muted": "#999999",
      "--light-muted": "#999999",
      "--sidebar-bg": "#F0F0F0",
      "--card-bg": "#FFFFFF",
      "--nav-active-bg": "#F0F0F0",
      "--nav-active-color": "#C4790A",
      "--purple-soft": "#F0F0F0",
    }
  },
  dusk: {
    id: "dusk",
    name: "Dusk",
    description: "Deep purples and night tones",
    tier: 1,
    unlockRequirement: { type: "credits", amount: 500 },
    free: false,
    variables: {
      "--bg": "#150f1e",
      "--surface": "#1e1428",
      "--border": "#2d1f4a",
      "--text": "#e9d5ff",
      "--muted": "#9f7aea",
      "--light-muted": "#6d28d9",
      "--purple-soft": "#2d1f4a",
      "--sidebar-bg": "#1a1228",
      "--card-bg": "#1e1428",
      "--nav-active-bg": "#2d1f4a",
      "--nav-active-color": "#a78bfa"
    }
  },
  forest: {
    id: "forest",
    name: "Forest",
    description: "Deep greens, earthy and calm",
    tier: 1,
    unlockRequirement: { type: "credits", amount: 500 },
    free: false,
    variables: {
      "--bg": "#0a1209",
      "--surface": "#0f1a12",
      "--border": "#14401e",
      "--text": "#dcfce7",
      "--muted": "#4ade80",
      "--light-muted": "#166534",
      "--purple-soft": "#14401e",
      "--sidebar-bg": "#0d1710",
      "--card-bg": "#0f1a12",
      "--nav-active-bg": "#14401e",
      "--nav-active-color": "#4ade80"
    }
  },
  goldenHour: {
    id: "goldenHour",
    name: "Golden Hour",
    description: "Amber and gold, warm evening",
    tier: 2,
    unlockRequirement: { type: "credits", amount: 1500 },
    free: false,
    variables: {
      "--bg": "#120e06",
      "--surface": "#1a1409",
      "--border": "#3d2a08",
      "--text": "#fef3c7",
      "--muted": "#f59e0b",
      "--light-muted": "#92400e",
      "--purple-soft": "#3d2a08",
      "--sidebar-bg": "#160f05",
      "--card-bg": "#1a1409",
      "--nav-active-bg": "#3d2a08",
      "--nav-active-color": "#f59e0b"
    }
  },
  midnight: {
    id: "midnight",
    name: "Midnight",
    description: "Pure dark mode, sleek and modern",
    tier: 2,
    unlockRequirement: { type: "credits", amount: 1500 },
    free: false,
    variables: {
      "--bg": "#0a0a0a",
      "--surface": "#141414",
      "--border": "#1e1e1e",
      "--text": "#f1f5f9",
      "--muted": "#64748b",
      "--light-muted": "#475569",
      "--purple-soft": "#1e1e2e",
      "--sidebar-bg": "#0f0f0f",
      "--card-bg": "#141414",
      "--nav-active-bg": "#1e1e2e",
      "--nav-active-color": "#a78bfa"
    }
  },
  aurora: {
    id: "aurora",
    name: "Aurora",
    description: "Deep space with iridescent glow — founding members only",
    tier: 3,
    unlockRequirement: { type: "founding_member" },
    free: false,
    variables: {
      "--bg": "#08040f",
      "--surface": "#130820",
      "--border": "#2e0f3e",
      "--text": "#fae8ff",
      "--muted": "#d946ef",
      "--light-muted": "#86198f",
      "--purple-soft": "#2e0f3e",
      "--sidebar-bg": "#0d0614",
      "--card-bg": "#130820",
      "--nav-active-bg": "#2e0f3e",
      "--nav-active-color": "#f0abfc"
    }
  },
  noir: {
    id: "noir",
    name: "Noir",
    description: "Rich crimson and gold — founding members only",
    tier: 3,
    unlockRequirement: { type: "founding_member" },
    free: false,
    variables: {
      "--bg": "#0f0505",
      "--surface": "#1a0a0a",
      "--border": "#3f0808",
      "--text": "#fee2e2",
      "--muted": "#fbbf24",
      "--light-muted": "#7f1d1d",
      "--purple-soft": "#3f0808",
      "--sidebar-bg": "#1a0a0a",
      "--card-bg": "#1a0a0a",
      "--nav-active-bg": "#3f0808",
      "--nav-active-color": "#fbbf24"
    }
  },
  architect: {
    id: "architect",
    name: "Architect",
    description: "Deep navy and white gold — the one who drew the plans",
    tier: 4,
    unlockRequirement: { type: "founder" },
    free: false,
    founderOnly: true,
    variables: {
      "--bg": "#040810",
      "--surface": "#070e1c",
      "--border": "#0d1f3c",
      "--text": "#e8d5a3",
      "--muted": "#c9a84c",
      "--light-muted": "#7a6030",
      "--purple-soft": "#0d1f3c",
      "--sidebar-bg": "#050c18",
      "--card-bg": "#070e1c",
      "--nav-active-bg": "#0d1f3c",
      "--nav-active-color": "#e8d5a3"
    }
  },
  origin: {
    id: "origin",
    name: "Origin",
    description: "Where it all began — pure black shifting to ember",
    tier: 4,
    unlockRequirement: { type: "founder" },
    free: false,
    founderOnly: true,
    variables: {
      "--bg": "#050505",
      "--surface": "#0d0805",
      "--border": "#1a0f08",
      "--text": "#fff1e6",
      "--muted": "#c2410c",
      "--light-muted": "#7c2d12",
      "--purple-soft": "#1a0f08",
      "--sidebar-bg": "#080503",
      "--card-bg": "#0d0805",
      "--nav-active-bg": "#1a0f08",
      "--nav-active-color": "#fb923c"
    }
  },
  hundred: {
    id: "hundred",
    name: "100",
    description: "The brand itself — cream gold on near-black, platform gradient accents",
    tier: 4,
    unlockRequirement: { type: "founder" },
    free: false,
    founderOnly: true,
    variables: {
      "--bg": "#08050f",
      "--surface": "#100a1a",
      "--border": "#2a1245",
      "--text": "#f5e6d0",
      "--muted": "#c084fc",
      "--light-muted": "#7c3aed",
      "--purple-soft": "#2a1245",
      "--sidebar-bg": "#0a0614",
      "--card-bg": "#100a1a",
      "--nav-active-bg": "#2a1245",
      "--nav-active-color": "#f59e0b"
    }
  }
};

/* =========================================
   CHALLENGE DEFINITIONS
========================================= */

export const WEEKLY_CHALLENGES = [
  {
    id: "first_viewer",
    name: "First viewer",
    description: "Be in the first 10 views on any drop this week",
    icon: "👁",
    reward: 15,
    target: 1,
    actionType: "early_view"
  },
  {
    id: "conversation_starter",
    name: "Conversation starter",
    description: "Leave 5 meaningful comments on creator drops",
    icon: "💬",
    reward: 10,
    target: 5,
    actionType: "comment"
  },
  {
    id: "new_discovery",
    name: "New discovery",
    description: "Follow 2 creators you've never interacted with",
    icon: "⭐",
    reward: 20,
    target: 2,
    actionType: "follow_new"
  },
  {
    id: "drop_supporter",
    name: "Drop supporter",
    description: "Unlock a paid drop before it hits 50 views",
    icon: "🔥",
    reward: 25,
    target: 1,
    actionType: "early_unlock"
  },
  {
    id: "spread_word",
    name: "Spread the word",
    description: "Share a creator drop link outside the platform",
    icon: "📣",
    reward: 30,
    target: 1,
    actionType: "share"
  }
];

export const MONTHLY_CHALLENGES = [
  {
    id: "superfan",
    name: "Superfan of the month",
    description: "Engage with 1 creator daily for 20 days",
    icon: "💎",
    reward: 150,
    bonusReward: "Superfan badge",
    target: 20,
    actionType: "daily_engagement"
  },
  {
    id: "early_adopter",
    name: "Early adopter",
    description: "Be in first 5 views on 10 different drops",
    icon: "🏆",
    reward: 200,
    bonusReward: "app look unlock",
    target: 10,
    actionType: "early_view_unique"
  },
  {
    id: "community_builder",
    name: "Community builder",
    description: "Refer 3 users who stay active 14+ days",
    icon: "🌟",
    reward: 300,
    bonusReward: "secret badge",
    target: 3,
    actionType: "referral"
  }
];

/* =========================================
   CREDIT STORE ITEMS
========================================= */

export const CREDIT_STORE = [
  {
    id: "boost_creator",
    name: "Boost a creator",
    description: "Push a creator's drop to top of Discover for 24 hours",
    icon: "🚀",
    cost: 100,
    category: "creator"
  },
  {
    id: "drop_alert",
    name: "Drop alert",
    description: "Get notified instantly when a specific creator posts",
    icon: "🔔",
    cost: 20,
    category: "creator"
  },
  {
    id: "super_comment",
    name: "Super comment",
    description: "Pin your comment to the top of a drop for 48 hours",
    icon: "⭐",
    cost: 30,
    category: "creator"
  },
  {
    id: "credit_tip_small",
    name: "Tip creator $1 CAD",
    description: "Convert 200 credits into a real $1 CAD tip to a creator",
    icon: "💝",
    cost: 200,
    category: "creator"
  },
  {
    id: "credit_tip_large",
    name: "Tip creator $5 CAD",
    description: "Convert 1000 credits into a real $5 CAD tip to a creator",
    icon: "💝",
    cost: 1000,
    category: "creator"
  },
  {
    id: "theme_dusk",
    name: "Unlock Dusk theme",
    description: "Deep purple night theme for your app",
    icon: "🎨",
    cost: 500,
    category: "cosmetic",
    themeId: "dusk"
  },
  {
    id: "theme_forest",
    name: "Unlock Forest theme",
    description: "Deep green earthy theme",
    icon: "🎨",
    cost: 500,
    category: "cosmetic",
    themeId: "forest"
  },
  {
    id: "theme_golden_hour",
    name: "Unlock Golden Hour theme",
    description: "Warm amber and gold evening theme",
    icon: "🎨",
    cost: 1500,
    category: "cosmetic",
    themeId: "goldenHour"
  },
  {
    id: "theme_midnight",
    name: "Unlock Midnight theme",
    description: "Pure sleek dark mode",
    icon: "🎨",
    cost: 1500,
    category: "cosmetic",
    themeId: "midnight"
  }
];

/* =========================================
   HELPER FUNCTIONS
========================================= */

function getTodayKey() {
  return new Date().toISOString().split("T")[0];
}

function getYesterdayKey() {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().split("T")[0];
}

function getWeekKey() {
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay() + 1);
  return startOfWeek.toISOString().split("T")[0];
}

function getMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function isTooSoon(lastActionTime, cooldownMs) {
  if (!lastActionTime) return false;
  return Date.now() - lastActionTime < cooldownMs;
}

function getAccountAgeDays(createdAt) {
  if (!createdAt?.seconds) return 0;
  const created = new Date(createdAt.seconds * 1000);
  return Math.floor((Date.now() - created.getTime()) / 86400000);
}

function calculateTrustScore(current = {}, accountAgeDays = 0) {
  let score = current.trustScore ?? 50;
  if (accountAgeDays >= 7) score += 5;
  if (accountAgeDays >= 30) score += 10;
  if ((current.streak || 0) >= 7) score += 5;
  if ((current.points || 0) >= 150) score += 5;
  if ((current.warningCount || 0) > 0) score -= current.warningCount * 10;
  return Math.max(0, Math.min(score, 100));
}

/* =========================================
   THEME UNLOCKING LOGIC
========================================= */

export function getUnlockedThemes(perks = {}) {
  const unlocked = ["warmCream", "cleanWhite"];

  Object.values(THEMES).forEach((theme) => {
    if (theme.free) return;

    // Founder-only themes (isFounder manually set in Firebase)
    if (theme.unlockRequirement?.type === "founder") {
      if (perks.isFounder === true) {
        unlocked.push(theme.id);
      }
      return;
    }

    // Founding member themes
    if (theme.unlockRequirement?.type === "founding_member") {
      if (perks.isFoundingMember || perks.isFounder) {
        unlocked.push(theme.id);
      }
      return;
    }

    // Credit-unlocked themes
    if (perks.unlockedThemes?.includes(theme.id)) {
      if (!unlocked.includes(theme.id)) {
        unlocked.push(theme.id);
      }
    }
  });

  return unlocked;
}

/* =========================================
   LEVEL AND BADGE CALCULATIONS
========================================= */

export function calculateUserLevel(perks = {}) {
  const points = perks.points || 0;
  if (points >= 1000) return "Core Member";
  if (points >= 500) return "Early Supporter";
  if (points >= 150) return "Active Supporter";
  if (points >= 50) return "Supporter";
  return "Viewer";
}

export function calculateBadges(perks = {}) {
  const badges = [];

  if (perks.isFoundingMember) {
    badges.push({ id: "founding-member", label: "Founding Member", icon: "🏆", className: "badge-gold" });
  }

  if ((perks.streak || 0) >= 7) {
    badges.push({ id: "seven-day-streak", label: "7 Day Streak", icon: "🔥", className: "badge-hot" });
  }

  if ((perks.streak || 0) >= 30) {
    badges.push({ id: "thirty-day-streak", label: "30 Day Streak", icon: "⚡", className: "badge-electric" });
  }

  if ((perks.points || 0) >= 150) {
    badges.push({ id: "active-supporter", label: "Active Supporter", icon: "✨", className: "badge-blue" });
  }

  if ((perks.points || 0) >= 500) {
    badges.push({ id: "early-supporter", label: "Early Supporter", icon: "🚀", className: "badge-purple" });
  }

  if ((perks.trustScore || 0) >= 80) {
    badges.push({ id: "trusted-member", label: "Trusted Member", icon: "🛡️", className: "badge-green" });
  }

  if (perks.superfanMonth) {
    badges.push({ id: "superfan", label: "Superfan", icon: "💎", className: "badge-diamond" });
  }

  return badges;
}

/* =========================================
   INITIALIZE USER PERKS
========================================= */

export async function getUserPerks(userId) {
  if (!userId) return null;
  const perksRef = doc(db, "userPerks", userId);
  const perksSnap = await getDoc(perksRef);
  if (!perksSnap.exists()) {
    // Auto-bootstrap the userPerks document on first access
    return await initializeUserPerks(userId);
  }
  return { id: perksSnap.id, ...perksSnap.data() };
}

export async function initializeUserPerks(userId) {
  if (!userId) return null;

  const perksRef = doc(db, "userPerks", userId);
  const perksSnap = await getDoc(perksRef);

  if (perksSnap.exists()) {
    return { id: perksSnap.id, ...perksSnap.data() };
  }

  const weekKey = getWeekKey();
  const monthKey = getMonthKey();

  const startingPerks = {
    userId,
    points: 25,
    credits: 25,
    dailyPoints: 25,
    dailyPointDate: getTodayKey(),
    streak: 1,
    currentStreak: 1,
    longestStreak: 1,
    lastCheckIn: null,
    monthlyCheckInCredits: 0,
    monthlyCheckInMonth: monthKey,
    starterPackPurchased: false,
    resetGraceUntil: null,
    monthlyFanReferrals: 0,
    monthlyCreatorReferrals: 0,
    monthlyFreeUnlocks: 0,
    monthlySpendMonth: monthKey,
    lastActiveDate: getTodayKey(),
    isFoundingMember: true,
    level: "Viewer",
    trustScore: 50,
    warningCount: 0,
    suspiciousActivityCount: 0,
    isRestricted: false,
    lastLikeSaveActionAt: 0,
    lastCommentActionAt: 0,
    unlockCredits: 0,
    theme: "warmCream",
    unlockedThemes: ["warmCream", "cleanWhite"],
    weeklyChallengeSeed: weekKey,
    monthlyChallengeMonth: monthKey,
    weeklyProgress: {},
    monthlyProgress: {},
    weeklyCompleted: [],
    monthlyCompleted: [],
    totalChallengesCompleted: 0,
    totalCreditsEarned: 25,
    totalCreditsSpent: 0,
    pipFinds: [],
    pipWardrobe: [],
    pipOutfit: { hat: null, shirt: null, pants: null, shoes: null, accessory: null },
    pipIntroSeen: false,
    fanOnboardingComplete: false,
    seenFollowTip: false,
    seenCreditsTip: false,
    seenCollectionTip: false,
    isPipMaster: false,
    isFormerPipMaster: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  startingPerks.badges = calculateBadges(startingPerks);

  await setDoc(perksRef, startingPerks, { merge: true });

  return startingPerks;
}

/* =========================================
   CHALLENGE PROGRESS TRACKING
========================================= */

export async function updateChallengeProgress(userId, actionType, value = 1) {
  if (!userId) return null;

  const perksRef = doc(db, "userPerks", userId);
  const perksSnap = await getDoc(perksRef);

  if (!perksSnap.exists()) return null;

  const current = perksSnap.data();
  const weekKey = getWeekKey();
  const monthKey = getMonthKey();

  let weeklyProgress = current.weeklyProgress || {};
  let weeklyCompleted = current.weeklyCompleted || [];
  let monthlyProgress = current.monthlyProgress || {};
  let monthlyCompleted = current.monthlyCompleted || [];
  let creditsEarned = 0;

  // Reset weekly if new week
  if (current.weeklyChallengeSeed !== weekKey) {
    weeklyProgress = {};
    weeklyCompleted = [];
  }

  // Reset monthly if new month
  if (current.monthlyChallengeMonth !== monthKey) {
    monthlyProgress = {};
    monthlyCompleted = [];
  }

  // Update weekly challenges
  WEEKLY_CHALLENGES.forEach((challenge) => {
    if (challenge.actionType !== actionType) return;
    if (weeklyCompleted.includes(challenge.id)) return;

    const current_progress = weeklyProgress[challenge.id] || 0;
    const newProgress = Math.min(current_progress + value, challenge.target);
    weeklyProgress[challenge.id] = newProgress;

    if (newProgress >= challenge.target) {
      weeklyCompleted.push(challenge.id);
      creditsEarned += challenge.reward;
    }
  });

  // Update monthly challenges
  MONTHLY_CHALLENGES.forEach((challenge) => {
    if (challenge.actionType !== actionType) return;
    if (monthlyCompleted.includes(challenge.id)) return;

    const current_progress = monthlyProgress[challenge.id] || 0;
    const newProgress = Math.min(current_progress + value, challenge.target);
    monthlyProgress[challenge.id] = newProgress;

    if (newProgress >= challenge.target) {
      monthlyCompleted.push(challenge.id);
      creditsEarned += challenge.reward;
    }
  });

  const newCredits = (current.credits || 0) + creditsEarned;
  const newTotalEarned = (current.totalCreditsEarned || 0) + creditsEarned;
  const newTotalCompleted = (current.totalChallengesCompleted || 0) +
    (weeklyCompleted.length - (current.weeklyCompleted || []).length) +
    (monthlyCompleted.length - (current.monthlyCompleted || []).length);

  const updated = {
    ...current,
    weeklyChallengeSeed: weekKey,
    monthlyChallengeMonth: monthKey,
    weeklyProgress,
    weeklyCompleted,
    monthlyProgress,
    monthlyCompleted,
    credits: newCredits,
    totalCreditsEarned: newTotalEarned,
    totalChallengesCompleted: newTotalCompleted,
    updatedAt: serverTimestamp()
  };

  await setDoc(perksRef, updated, { merge: true });

  if (creditsEarned > 0) {
    await logCreditTransaction(userId, creditsEarned, "challenge_complete", `Completed challenge`);
  }

  return { updated, creditsEarned };
}

/* =========================================
   DAILY STREAK CREDITS
========================================= */

export async function awardDailyLoginCredits(userId) {
  if (!userId) return null;

  const perksRef = doc(db, "userPerks", userId);
  const perksSnap = await getDoc(perksRef);

  // Auto-create if missing then reload
  if (!perksSnap.exists()) {
    await initializeUserPerks(userId);
    return null; // skip daily award on first creation
  }

  const current = perksSnap.data();
  const today = getTodayKey();

  if (current.lastDailyLoginDate === today) return null;

  const streak = current.streak || 1;
  let creditsToAward = 5;

  if (streak >= 30) creditsToAward = 25;
  else if (streak >= 14) creditsToAward = 15;
  else if (streak >= 7) creditsToAward = 10;

  const newCredits = (current.credits || 0) + creditsToAward;

  await updateDoc(perksRef, {
    credits: newCredits,
    lastDailyLoginDate: today,
    totalCreditsEarned: (current.totalCreditsEarned || 0) + creditsToAward,
    updatedAt: serverTimestamp()
  });

  await logCreditTransaction(userId, creditsToAward, "daily_login", `Day ${streak} login streak`);

  return creditsToAward;
}

/* =========================================
   SPEND CREDITS
========================================= */

export async function spendCredits(userId, amount, reason, metadata = {}) {
  if (!userId || amount <= 0) return { success: false, error: "Invalid params" };

  const perksRef = doc(db, "userPerks", userId);
  const perksSnap = await getDoc(perksRef);

  if (!perksSnap.exists()) return { success: false, error: "User not found" };

  const current = perksSnap.data();
  const currentCredits = current.credits || 0;

  if (currentCredits < amount) {
    return { success: false, error: "Not enough credits", credits: currentCredits };
  }

  const newCredits = currentCredits - amount;

  const updateData = {
    credits: newCredits,
    totalCreditsSpent: (current.totalCreditsSpent || 0) + amount,
    updatedAt: serverTimestamp()
  };

  // Handle theme unlock
  if (metadata.themeId) {
    const currentUnlocked = current.unlockedThemes || ["warmCream", "cleanWhite"];
    if (!currentUnlocked.includes(metadata.themeId)) {
      updateData.unlockedThemes = [...currentUnlocked, metadata.themeId];
    }
  }

  await updateDoc(perksRef, updateData);
  await logCreditTransaction(userId, -amount, reason, metadata.description || reason);

  return { success: true, credits: newCredits };
}

/* =========================================
   SET ACTIVE THEME
========================================= */

export async function setUserTheme(userId, themeId) {
  if (!userId || !THEMES[themeId]) return false;

  const perksRef = doc(db, "userPerks", userId);
  const perksSnap = await getDoc(perksRef);

  if (!perksSnap.exists()) return false;

  const current = perksSnap.data();
  const unlocked = getUnlockedThemes(current);

  if (!unlocked.includes(themeId)) return false;

  await Promise.all([
    updateDoc(perksRef, {
      theme: themeId,
      updatedAt: serverTimestamp(),
    }),
    updateDoc(doc(db, "users", userId), { theme: themeId }),
  ]);

  return true;
}

/* =========================================
   CREDIT TRANSACTION LOG
========================================= */

async function logCreditTransaction(userId, amount, type, description) {
  try {
    await addDoc(collection(db, "creditTransactions"), {
      userId,
      amount,
      type,
      description,
      createdAt: serverTimestamp()
    });
  } catch (error) {
    console.warn("Credit transaction log failed:", error);
  }
}

/* =========================================
   APPLY THEME TO DOM
========================================= */

function normalizeThemeVariables(variables = {}) {
  const normalized = { ...variables };
  if (normalized["--text-muted"] && !normalized["--muted"]) {
    normalized["--muted"] = normalized["--text-muted"];
  }
  if (normalized["--muted"] && !normalized["--text-muted"]) {
    normalized["--text-muted"] = normalized["--muted"];
  }
  return normalized;
}

export function applyTheme(themeId) {
  const theme = THEMES[themeId] || THEMES.warmCream;
  const resolvedId = theme.id || "warmCream";
  const root = document.documentElement;

  THEME_CSS_VAR_KEYS.forEach((key) => {
    root.style.removeProperty(key);
  });

  root.setAttribute("data-theme", resolvedId);

  const variables = normalizeThemeVariables(theme.variables);
  Object.entries(variables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
}

export function resetTheme() {
  applyTheme("warmCream");
}

/* =========================================
   EXISTING FUNCTIONS — UNCHANGED
========================================= */

export function isValidCommentForPoints(text = "") {
  const clean = text.trim();
  if (clean.length < 8) return false;
  if (/^(.)\1{5,}$/.test(clean)) return false;
  if (/^[🔥❤️😂😍👏💯\s]+$/.test(clean)) return false;
  return true;
}

async function flagSuspiciousActivity(userId, current, reason) {
  const updated = {
    ...current,
    suspiciousActivityCount: (current.suspiciousActivityCount || 0) + 1,
    warningCount: (current.warningCount || 0) + 1,
    lastSuspiciousReason: reason,
    updatedAt: serverTimestamp()
  };

  updated.trustScore = calculateTrustScore(updated, updated.accountAgeDays || 0);
  updated.isRestricted = updated.warningCount >= 5 || updated.trustScore <= 20;
  updated.badges = calculateBadges(updated);

  await setDoc(doc(db, "userPerks", userId), updated, { merge: true });

  return { ...updated, pointBlockedReason: reason };
}

export async function trackUserActivity(
  userId,
  pointsToAdd = 5,
  actionType = "general",
  options = {}
) {
  if (!userId) return null;

  const today = getTodayKey();
  const yesterday = getYesterdayKey();
  const perksRef = doc(db, "userPerks", userId);
  const perksSnap = await getDoc(perksRef);

  let current = perksSnap.exists()
    ? perksSnap.data()
    : {
        userId,
        points: 0,
        credits: 0,
        dailyPoints: 0,
        dailyPointDate: today,
        streak: 0,
        trustScore: 50,
        warningCount: 0,
        suspiciousActivityCount: 0,
        isRestricted: false,
        isFoundingMember: true,
        unlockCredits: 0,
        theme: "warmCream",
        unlockedThemes: ["warmCream", "cleanWhite"]
      };

  if (current.isRestricted) {
    return { ...current, pointBlockedReason: "restricted-account" };
  }

  const accountAgeDays = getAccountAgeDays(current.createdAt);
  const isNewAccount = accountAgeDays < NEW_ACCOUNT_DAYS;
  const dailyCap = isNewAccount ? NEW_ACCOUNT_DAILY_CAP : DAILY_POINT_CAP;
  const dailyPoints = current.dailyPointDate === today ? current.dailyPoints || 0 : 0;

  if (dailyPoints >= dailyCap) {
    return flagSuspiciousActivity(userId, current, "daily-cap");
  }

  if (actionType === "comment" && isTooSoon(current.lastCommentActionAt, COMMENT_COOLDOWN_MS)) {
    return flagSuspiciousActivity(userId, current, "comment-cooldown");
  }

  if ((actionType === "like" || actionType === "save") && isTooSoon(current.lastLikeSaveActionAt, LIKE_SAVE_COOLDOWN_MS)) {
    return flagSuspiciousActivity(userId, current, "reaction-cooldown");
  }

  if (actionType === "comment" && !isValidCommentForPoints(options.text || "")) {
    return flagSuspiciousActivity(userId, current, "low-quality-comment");
  }

  const adjustedPoints = isNewAccount ? Math.ceil(pointsToAdd / 2) : pointsToAdd;
  const allowedPoints = Math.min(adjustedPoints, dailyCap - dailyPoints);

  let newStreak = current.streak || 0;
  if (current.lastActiveDate === today) {
    newStreak = current.streak || 1;
  } else if (current.lastActiveDate === yesterday) {
    newStreak = (current.streak || 0) + 1;
  } else {
    newStreak = 1;
  }

  // Award streak milestone credits
  let streakBonusCredits = 0;
  if (newStreak === 7 && current.streak < 7) streakBonusCredits = 50;
  if (newStreak === 14 && current.streak < 14) streakBonusCredits = 75;
  if (newStreak === 30 && current.streak < 30) streakBonusCredits = 150;
  if (newStreak === 100 && current.streak < 100) streakBonusCredits = 500;

  const newPoints = (current.points || 0) + allowedPoints;
  const newCredits = (current.credits || 0) + streakBonusCredits;
  const newUnlockCredits = Math.floor(newPoints / 500);

  const updatedPerks = {
    ...current,
    userId,
    points: newPoints,
    credits: newCredits,
    dailyPoints: dailyPoints + allowedPoints,
    dailyPointDate: today,
    streak: newStreak,
    lastActiveDate: today,
    accountAgeDays,
    unlockCredits: newUnlockCredits,
    isFoundingMember: current.isFoundingMember ?? true,
    theme: current.theme || "warmCream",
    unlockedThemes: current.unlockedThemes || ["warmCream", "cleanWhite"],
    updatedAt: serverTimestamp()
  };

  if (actionType === "comment") updatedPerks.lastCommentActionAt = Date.now();
  if (actionType === "like" || actionType === "save") updatedPerks.lastLikeSaveActionAt = Date.now();

  updatedPerks.trustScore = calculateTrustScore(updatedPerks, accountAgeDays);
  updatedPerks.isRestricted = updatedPerks.warningCount >= 5 || updatedPerks.trustScore <= 20;
  updatedPerks.level = calculateUserLevel(updatedPerks);
  updatedPerks.badges = calculateBadges(updatedPerks);

  await setDoc(perksRef, updatedPerks, { merge: true });

  // Also update challenge progress
  await updateChallengeProgress(userId, actionType);

  if (streakBonusCredits > 0) {
    await logCreditTransaction(userId, streakBonusCredits, "streak_milestone", `${newStreak} day streak bonus`);
  }

  return updatedPerks;
}
